import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import EmailIcon from "@material-ui/icons/Email";

import List from "../../components/List";
import Sidebar from "../../components/SideBar";
import EventApi from "../../services/event";
import { config } from "../../config";
import EditDialog from "./EditDialog";

// ── Formatters (shared with PendingSubscriptions) ─────────────────────────────

export function formatDocument(cpf) {
    if (!cpf) return "-";
    if (cpf.startsWith("PASS-")) {
        return cpf.replace("PASS-", "");
    }
    const digits = cpf.replace(/\D/g, "");
    if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
}

export function formatDate(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Column definitions ────────────────────────────────────────────────────────

function buildColumns(sponsorsMap) {
    return [
        { title: "Nome", field: "name" },
        { title: "E-mail", field: "email" },
        {
            title: "CPF / Passaporte",
            field: "cpf",
            render: (row) => formatDocument(row.cpf),
        },
        { title: "Empresa", field: "company" },
        { title: "Cargo", field: "role" },
        {
            title: "Patrocinador",
            field: "sponsor_id",
            render: (row) => (row.sponsor_id ? (sponsorsMap[row.sponsor_id] || "-") : ""),
        },
        {
            title: "Data de Inscrição",
            field: "created_at",
            render: (row) => formatDate(row.created_at),
        },
        {
            title: "Data de Aprovação",
            field: "approved_at",
            render: (row) => formatDate(row.approved_at),
        },
    ];
}

// ── Component ─────────────────────────────────────────────────────────────────

const ApprovedSubscriptions = (props) => {
    const eventId =
        props?.computedMatch?.params?.event_id ||
        props?.match?.params?.event_id ||
        1;

    const tableRef = useRef(null);
    const [sponsorsMap, setSponsorsMap] = useState({});

    // Carrega patrocinadores uma vez para exibir o nome na coluna
    useEffect(() => {
        EventApi.listSponsors({ event_id: eventId }).then((resp) => {
            if (resp?.data?.data) {
                const map = {};
                resp.data.data.forEach((s) => { map[s.id] = s.name; });
                setSponsorsMap(map);
            }
        }).catch(() => {});
    }, [eventId]);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        person: null,
    });

    const [editDialog, setEditDialog] = useState({
        open: false,
        person: null,
    });

    const [resendDialog, setResendDialog] = useState({
        open: false,
        person: null,
    });

    // ── Data fetching ──────────────────────────────────────────────────────

    const getRows = async (page = 1, perPage = 10, search = "") => {
        try {
            const params = {
                page,
                perPage,
                solicitation_approved: true,
            };
            if (search) {
                const trimmed = search.trim();
                const onlyDigits = trimmed.replace(/\D/g, "");
                const emailRegex = /^[^\s@]+@[^\s@]+/;
                if (emailRegex.test(trimmed)) {
                    params.email = trimmed;
                } else if (onlyDigits.length >= 6 && trimmed === onlyDigits) {
                    params.cpf = trimmed;
                } else {
                    params.name = trimmed;
                }
            }

            const resp = await EventApi.getAllPersonByEvent(eventId, params);
            if (!resp || !resp.data) {
                return { rows: [], total: 0 };
            }

            const data = resp.data.data || (Array.isArray(resp.data) ? resp.data : []);
            const total = resp.data.total || data.length;

            return { rows: data, total };
        } catch (error) {
            console.error("Erro ao carregar inscrições aprovadas:", error);
            toast.error("Erro ao carregar inscrições aprovadas");
            return { rows: [], total: 0 };
        }
    };

    // ── Confirm dialog (Delete) ────────────────────────────────────────────

    function openConfirmDialog(person) {
        setConfirmDialog({ open: true, person });
    }

    function closeConfirmDialog() {
        setConfirmDialog({ open: false, person: null });
    }

    async function handleDelete() {
        const { person } = confirmDialog;
        closeConfirmDialog();

        try {
            const resp = await EventApi.deletePerson(person.id);

            if (resp && (resp.data || resp.status === 200)) {
                toast.success("Inscrição excluída com sucesso");
                if (tableRef.current) tableRef.current.onQueryChange();
            } else {
                toast.error("Erro ao excluir inscrição. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao excluir:", error);
            toast.error("Erro ao excluir inscrição. Tente novamente");
        }
    }

    // ── Edit dialog ────────────────────────────────────────────────────────

    function openEditDialog(person) {
        setEditDialog({ open: true, person });
    }

    function closeEditDialog() {
        setEditDialog({ open: false, person: null });
    }

    function handleSaveSuccess() {
        closeEditDialog();
        toast.success("Dados atualizados com sucesso");
        if (tableRef.current) tableRef.current.onQueryChange();
    }

    // ── Resend email dialog ────────────────────────────────────────────

    function openResendDialog(person) {
        setResendDialog({ open: true, person });
    }

    function closeResendDialog() {
        setResendDialog({ open: false, person: null });
    }

    async function handleResendEmail() {
        const { person } = resendDialog;
        closeResendDialog();

        try {
            const qrcode = `${config.QRCODE_HOST}/?size=200x200&data=${person.id}`;
            const resp = await EventApi.sendConfirmationMail(person.name, person.email, qrcode);

            if (resp && !resp.response?.status) {
                toast.success(`E-mail de confirmação reenviado para ${person.email}`);
            } else {
                toast.error("Erro ao reenviar e-mail. Tente novamente");
            }
        } catch (error) {
            console.error("Erro ao reenviar e-mail:", error);
            toast.error("Erro ao reenviar e-mail. Tente novamente");
        }
    }

    // ── Actions ────────────────────────────────────────────────────────────

    const actions = [
        {
            icon: () => <EditIcon style={{ color: "#3b82f6" }} />,
            tooltip: "Editar inscrição",
            onClick: (e, person) => openEditDialog(person),
        },
        {
            icon: () => <EmailIcon style={{ color: "#f59e0b" }} />,
            tooltip: "Reenviar e-mail de confirmação",
            onClick: (e, person) => openResendDialog(person),
        },
        {
            icon: () => <DeleteIcon style={{ color: "#ef4444" }} />,
            tooltip: "Excluir inscrição",
            onClick: (e, person) => openConfirmDialog(person),
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────

    const page = (
        <>
            <List
                tableRef={tableRef}
                columns={buildColumns(sponsorsMap)}
                actions={actions}
                getRows={getRows}
            />

            {/* Confirmation Dialog — Delete */}
            <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja realmente excluir a inscrição aprovada de{" "}
                        <strong>{confirmDialog.person?.name}</strong>? Esta ação não
                        pode ser desfeita
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="default">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="secondary"
                        variant="contained"
                        autoFocus
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resend Email Confirmation Dialog */}
            <Dialog open={resendDialog.open} onClose={closeResendDialog}>
                <DialogTitle>Reenviar E-mail de Confirmação</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja reenviar o e-mail de confirmação para{" "}
                        <strong>{resendDialog.person?.name}</strong> ({resendDialog.person?.email})?{" "}
                        Um novo QR code será enviado.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeResendDialog} color="default">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleResendEmail}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Reenviar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <EditDialog
                open={editDialog.open}
                person={editDialog.person}
                onClose={closeEditDialog}
                onSave={handleSaveSuccess}
            />
        </>
    );

    return <Sidebar current="Inscrições Aprovadas" content={page} />;
};

export default ApprovedSubscriptions;
